import * as React from "react"
import { Check, ChevronsUpDown, Plus } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export function ComboboxWithAdd({
  options = [],
  value,
  onValueChange,
  onAddNew,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  addNewText = "Add new",
  className,
  disabled = false,
}) {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")

  const handleSelect = (selectedValue) => {
    onValueChange(selectedValue === value ? "" : selectedValue)
    setOpen(false)
    setSearchValue("")
  }

  const handleAddNew = () => {
    if (searchValue.trim() && onAddNew) {
      onAddNew(searchValue.trim())
      onValueChange(searchValue.trim())
      setSearchValue("")
      setOpen(false)
    }
  }

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchValue.toLowerCase())
  )

  const showAddNew = searchValue.trim() && 
    !options.some(opt => opt.toLowerCase() === searchValue.toLowerCase()) &&
    onAddNew

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between bg-white text-[#1A1A1A] hover:bg-white border-input",
            !value && "text-muted-foreground",
            className
          )}
        >
          {value || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0 border-2 border-[#1E3A5F] rounded-xl shadow-[4px_4px_0px_0px_#1E3A5F]" align="start">
        <Command>
          <CommandInput 
            placeholder={searchPlaceholder} 
            value={searchValue}
            onValueChange={setSearchValue}
            className="bg-white"
          />
          <CommandList>
            <CommandEmpty>
              {showAddNew ? (
                <button
                  onClick={handleAddNew}
                  className="flex items-center gap-2 w-full px-2 py-2 text-sm text-[#1E3A5F] hover:bg-[#FFEDDF] cursor-pointer"
                >
                  <Plus className="h-4 w-4" />
                  {addNewText}: "{searchValue}"
                </button>
              ) : (
                emptyText
              )}
            </CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option}
                  value={option}
                  onSelect={() => handleSelect(option)}
                  className="text-[#1A1A1A] cursor-pointer"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option}
                </CommandItem>
              ))}
            </CommandGroup>
            {showAddNew && filteredOptions.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={handleAddNew}
                    className="text-[#1E3A5F] cursor-pointer"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {addNewText}: "{searchValue}"
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
